import React, { useEffect, useState } from 'react';
import SystemDashboard from 'layouts/SystemDashboard';
import {
  Box,
  Breadcrumbs,
  Grid,
  TextField,
  Button,
  Typography,
  Link,
} from '@mui/material';
import {
  getAttachment,
  saveAttachment,
  updateAttachment,
  getFile,
} from './AttachmentService';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import FileUpload from './FileUpload';
import { notify } from 'utils/notify';
import { extractErrorMessage } from 'utils/extractErrorMessage';

interface Attachment {
  name: string;
  filename: string;
  contentType: string;
  size: string;
}

const AttachmentDetail = (): JSX.Element => {
  const [name, setName] = useState('');
  const [filename, setFilename] = useState('');
  const [contentType, setContentType] = useState('');
  const [size, setSize] = useState('');
  const [imageSrc, setImageSrc] = useState('');
  const [errors, setErrors] = useState<{ name?: string }>({});
  const navigate = useNavigate();
  const { attachmentid } = useParams<{ attachmentid: string }>();

  const validate = (): boolean => {
    const newErrors: { name?: string } = {};
    if (!name) {
      newErrors.name = 'Name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadLoadedFileData = (
    filename: string,
    contentType: string,
    size: string,
  ) => {
    getFile(filename)
      .then((imageResponse) => {
        const imageData = URL.createObjectURL(new Blob([imageResponse.data]));
        console.log('getFile imageData...', imageData);
        console.log('getFile filename...', filename);
        console.log('getFile contentType...', contentType);
        console.log('getFile size...', size);
        setImageSrc(imageData);
        setFilename(filename);
        setContentType(contentType);
        setSize(size);
      })
      .catch((error) => {
        console.error(error);
        notify(extractErrorMessage(error));
      });
  };

  const saveOrUpdateAttachment = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const attachment: Attachment = { name, filename, contentType, size };

    if (attachmentid) {
      updateAttachment(attachmentid, attachment)
        .then(() => {
          navigate('/system/dashboard/attachments');
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    } else {
      saveAttachment(attachment)
        .then(() => {
          navigate('/system/dashboard/attachments');
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    }
  };

  useEffect(() => {
    if (attachmentid) {
      getAttachment(attachmentid)
        .then((response) => {
          console.log('getAttachment DATA...', response.data);
          const attachment: Attachment = response.data;
          setName(attachment.name);
          setFilename(attachment.filename);
          setContentType(attachment.contentType);
          setSize(attachment.size);
          return getFile(attachment.filename);
        })
        .then((imageResponse) => {
          const imageData = URL.createObjectURL(new Blob([imageResponse.data]));
          setImageSrc(imageData);
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    }
  }, [attachmentid]);

  return (
    <Container>
      <SystemDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              System Content
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/system/dashboard/attachments"
            >
              Attachments
            </Link>
            <Typography color="text.primary">Attachment Detail</Typography>
          </Breadcrumbs>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <Typography variant="h4">Attachment Detail</Typography>
        </Box>
        <Box>
          <form onSubmit={saveOrUpdateAttachment}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Name
                </Typography>
                <TextField
                  variant="outlined"
                  name="name"
                  fullWidth
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <FileUpload uploadLoadedFileData={uploadLoadedFileData} />
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  File Name
                </Typography>
                <TextField
                  margin="dense"
                  id="filename"
                  type="text"
                  fullWidth
                  variant="standard"
                  value={filename}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Content Type
                </Typography>
                <TextField
                  margin="dense"
                  id="contentType"
                  type="text"
                  fullWidth
                  variant="standard"
                  value={contentType}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Size
                </Typography>
                <TextField
                  margin="dense"
                  id="size"
                  type="text"
                  fullWidth
                  variant="standard"
                  value={size}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  marginBottom={4}
                >
                  <Button
                    onClick={() => navigate('/system/dashboard/attachments')}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
        <Box>
          {imageSrc && (
            <img
              src={imageSrc}
              alt="Attachment"
              style={{ maxWidth: '100%', marginTop: '20px' }}
            />
          )}
        </Box>
      </SystemDashboard>
    </Container>
  );
};

export default AttachmentDetail;
